import { format } from 'date-fns';
import { CreditCard, TrendingUp, Users, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import {
  type Subscription,
  useBillingCheckoutReadiness,
  useBillingSummary,
  useSubscriptions,
} from '../hooks/useAdminData';
import { formatBillingProvider } from '../lib/billing-provider';
import { formatCurrency } from '../lib/currency';
import { navigate } from '../lib/navigation';
import { subscriptionStatusLabel } from '../lib/subscriptionStatus';

const PAGE_SIZE = 20;

const PLAN_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'professional', label: 'Professional' },
  { value: 'business', label: 'Business' },
];

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trialing' },
  { value: 'past_due', label: 'Past due' },
  { value: 'paused', label: 'Paused' },
  { value: 'canceled', label: 'Canceled' },
];

const PROVIDER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'stripe', label: 'Stripe' },
  { value: 'wompi', label: 'Wompi' },
  { value: 'manual', label: 'Manual' },
];

function formatProviderMrr(amount: number, currencyCode: string): string {
  const code = currencyCode.toUpperCase();
  if (code === 'USD') return formatUsd(amount);
  return `${code} ${amount.toLocaleString('en-US')}`;
}

function planBadgeVariant(plan: string): 'default' | 'info' | 'success' | 'primary' {
  switch (plan.toLowerCase()) {
    case 'starter':
    case 'pro':
      return 'info';
    case 'growth':
    case 'scale':
      return 'success';
    case 'enterprise':
    case 'professional':
    case 'business':
      return 'primary';
    default:
      return 'default';
  }
}

function statusBadgeVariant(status: string): 'success' | 'warning' | 'danger' | 'default' {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success';
    case 'trialing':
    case 'past_due':
    case 'paused':
      return 'warning';
    case 'canceled':
    case 'expired':
      return 'danger';
    default:
      return 'default';
  }
}

function formatUsd(amount: number): string {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function Subscriptions() {
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [page, setPage] = useState(1);

  const params = useMemo(() => {
    const p: Record<string, unknown> = { page, limit: PAGE_SIZE };
    if (planFilter) p.plan = planFilter;
    if (statusFilter) p.status = statusFilter;
    if (providerFilter) p.provider = providerFilter;
    return p;
  }, [page, planFilter, statusFilter, providerFilter]);

  const summaryParams = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (providerFilter) p.provider = providerFilter;
    return p;
  }, [providerFilter]);

  const { data, isLoading } = useSubscriptions(params);
  const { data: summary, isLoading: summaryLoading } = useBillingSummary(summaryParams);
  const { data: checkoutReadiness, isLoading: readinessLoading } =
    useBillingCheckoutReadiness();

  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-text">Subscriptions</h1>
        <p className="text-sm text-text-muted mt-1">
          Recurring subscription revenue — distinct from the lead-sales revenue shown in Platform
          Health.
        </p>
        <p className="text-xs text-text-muted mt-2 max-w-3xl">
          Estimated MRR/ARR are computed from plan prices; Actual is stored billing payments
          received during the selected window.
        </p>
      </div>

      <section
        aria-live="polite"
        className="bg-surface rounded-xl border border-border px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h2 className="text-sm font-semibold text-text">
            {readinessLoading
              ? 'Checking Wompi checkout readiness'
              : checkoutReadiness?.ready
                ? 'Wompi checkout ready'
                : 'Wompi checkout unavailable'}
          </h2>
          <p className="text-xs text-text-muted mt-1">
            Configuration status only. Merchant secrets are never returned to the admin client.
          </p>
          {checkoutReadiness?.issues.length ? (
            <p className="text-xs text-warning mt-2">
              {checkoutReadiness.issues.map((issue) => issue.replaceAll('_', ' ')).join(' · ')}
            </p>
          ) : null}
        </div>
        {checkoutReadiness ? (
          <Badge variant={checkoutReadiness.ready ? 'success' : 'warning'}>
            {checkoutReadiness.environment.charAt(0).toUpperCase() +
              checkoutReadiness.environment.slice(1)}
          </Badge>
        ) : null}
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="MRR (estimated)"
          caption="from plan prices"
          value={summary ? formatProviderMrr(summary.mrr, summary.currency) : '-'}
          icon={CreditCard}
          loading={summaryLoading}
        />
        <StatCard
          label="ARR (estimated)"
          caption="from plan prices"
          value={summary ? formatProviderMrr(summary.arr, summary.currency) : '-'}
          icon={TrendingUp}
          loading={summaryLoading}
        />
        <StatCard
          label="Active Subscriptions"
          value={summary?.activeSubscriptions ?? 0}
          icon={Users}
          loading={summaryLoading}
        />
        <StatCard
          label={`Actual collected (${summary?.collectedRevenueWindowDays ?? 30}d)`}
          caption="stored billing payments"
          value={
            summary && summary.collectedRevenue.length > 0
              ? summary.collectedRevenue
                  .map((c) => formatCurrency(c.amount, c.currencyCode))
                  .join(' · ')
              : formatUsd(0)
          }
          icon={Wallet}
          loading={summaryLoading}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <span>Plan</span>
          <select
            aria-label="Plan"
            value={planFilter}
            onChange={(e) => {
              setPlanFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text"
          >
            <option value="">All plans</option>
            {PLAN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <span>Status</span>
          <select
            aria-label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <span>Provider</span>
          <select
            aria-label="Provider"
            value={providerFilter}
            onChange={(e) => {
              setProviderFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text"
          >
            <option value="">All providers</option>
            {PROVIDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {summary?.byProvider && summary.byProvider.length > 0 && (
        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-semibold text-text">MRR by provider</h2>
          <p className="text-xs text-text-muted mt-1">
            Each provider bills in its own currency — figures are provider/currency-scoped, not
            FX-normalized into a single blended number.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {summary.byProvider.map((entry) => (
              <div
                key={entry.provider}
                className="rounded-lg border border-border px-4 py-3 flex items-center justify-between"
              >
                <div>
                  <p className="text-sm text-text font-medium capitalize">
                    {formatBillingProvider(entry.provider)}
                  </p>
                  <p className="text-xs text-text-muted">{entry.activeSubscriptions} active</p>
                </div>
                <p className="text-sm font-semibold text-text">
                  {formatProviderMrr(entry.mrr, entry.currency)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-5 py-3 font-medium">Organization</th>
                <th className="px-5 py-3 font-medium">Plan</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Provider</th>
                <th className="px-5 py-3 font-medium">Current Period</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                    Loading…
                  </td>
                </tr>
              ) : data?.data?.length ? (
                data.data.map((sub: Subscription) => (
                  <tr
                    key={sub.id}
                    className="border-b border-border last:border-0 hover:bg-surface-hover cursor-pointer"
                    onClick={() => navigate(`/organizations/${sub.organizationId}`)}
                  >
                    <td className="px-5 py-3 text-text font-medium">{sub.organizationName}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <Badge variant={planBadgeVariant(sub.plan)}>{sub.plan}</Badge>
                        {sub.pendingPlan && (
                          <span className="text-xs text-text-muted">→ {sub.pendingPlan}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={statusBadgeVariant(sub.status)}>
                        {subscriptionStatusLabel(sub.status, sub.planExpiredAt)}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-text-secondary">
                      {formatBillingProvider(sub.provider)}
                    </td>
                    <td className="px-5 py-3 text-text-muted text-xs whitespace-nowrap">
                      {format(new Date(sub.currentPeriodStart), 'MMM d')} –{' '}
                      {format(new Date(sub.currentPeriodEnd), 'MMM d, yyyy')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                    No subscriptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-text-muted">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
